import { createApi } from '@reduxjs/toolkit/query/react';
import { omit } from 'lodash';
import { Endpoint } from 'services/endpoints';

import { FormType } from 'pages/merchant/edit/sections/kyb/keys';

import { HTTPMethod, Response, SuccessResponse } from 'types/http';

import { getDefaultPagination } from '../../utils/pagination';
import { TAGS as ACCOUNT_API_TAGS } from '../accounts/keys';
import { fetchBaseQueryWithAuth } from '../api';
import { TAGS } from './keys';
import {
  AssociatedPerson,
  AssociatedPersonResponse,
  AssociatedPersonsListResponse,
  AutoCreateMerchantBankAccountResponse,
  BridgeKycInfoData,
  BridgeKycInfoResponse,
  CreateAssociatedPersonPayload,
  CreateAssociatedPersonResponse,
  CreateBankAccountPayload,
  CreateMerchantBankAccountResponse,
  CreateMerchantPayload,
  CryptoPayoutRequest,
  CryptoPayoutResponse,
  FiatPayoutSettingsPayload,
  MerchantInfo,
  MerchantsInfoResponse,
  MerchantsListQuery,
  MerchantsListResponse,
  MerchantsPayoutDestinationsResponse,
  PayoutDestinationType,
  RefundReserveResponse,
  SimulateDepositResponse,
  TitanKybDataResponse,
  UpdateAssociatedPersonPayload,
  UpdateAssociatedPersonResponse,
} from './types';
import { decorateUpdateRequest, undecorateMerchantData } from './utils';

const REDUCER_PATH = 'merchantsApi';

export const merchantsApi = createApi({
  reducerPath: REDUCER_PATH,
  baseQuery: fetchBaseQueryWithAuth,
  tagTypes: [TAGS.MERCHANT_LIST, TAGS.MERCHANT_BANK_ACCOUNTS_LIST, TAGS.MERCHANT_KYB, TAGS.ASSOCIATED_PERSONS, TAGS.REFUND_RESERVE],
  endpoints: (builder) => ({
    getMerchantsList: builder.query<MerchantsListResponse, MerchantsListQuery>({
      query: (requestBody) => ({
        url: Endpoint.Merchants.List,
        method: HTTPMethod.POST,
        body: {
          ...requestBody,
          filters: {
            ...requestBody.filters,
          },
        },
      }),
      transformResponse: (response: Response<MerchantsListResponse>) => {
        if (!response?.data) {
          return { data: [], pagination: getDefaultPagination(), requestId: response.requestId };
        }

        return response.data;
      },
      providesTags: [TAGS.MERCHANT_LIST],
    }),
    getMerchant: builder.query<MerchantsInfoResponse, string>({
      query: (uuid) => Endpoint.Merchants.ById(uuid),
      transformResponse: (response: MerchantsInfoResponse) => ({
        data: response.data ? undecorateMerchantData(response?.data) : (response.data as MerchantInfo),
      }),
      providesTags: [TAGS.MERCHANT_KYB],
    }),
    getMerchantOnboardingToken: builder.query<{ onboardingToken: string | null }, string>({
      query: (uuid) => Endpoint.Merchants.OnboardingToken(uuid),
      transformResponse: (response: { data: { onboardingToken: string | null } }) => response.data,
    }),
    updateMerchant: builder.mutation<Response<SuccessResponse>, Partial<MerchantInfo>>({
      query: (requestBody) => ({
        url: Endpoint.Merchants.ById(requestBody.uuid!),
        method: HTTPMethod.PUT,
        body: decorateUpdateRequest(requestBody),
      }),
      invalidatesTags: (result, error) => (error ? [] : [TAGS.MERCHANT_KYB]),
    }),
    createMerchant: builder.mutation<Response<SuccessResponse>, CreateMerchantPayload>({
      query: (body) => ({
        url: Endpoint.Merchants.Create,
        method: HTTPMethod.POST,
        body: { ...body, pwcEnabled: true },
      }),
      invalidatesTags: (result, error) => (error ? [] : [TAGS.MERCHANT_LIST, ACCOUNT_API_TAGS.ACCOUNT_LIST]),
    }),
    createMerchantsBankAccount: builder.mutation<CreateMerchantBankAccountResponse, CreateBankAccountPayload>({
      query: ({ uuid, ...body }) => ({
        url: Endpoint.Merchants.BankAccountsById(uuid!),
        method: HTTPMethod.POST,
        body,
      }),
      invalidatesTags: (result, error) => (error ? [] : [TAGS.MERCHANT_BANK_ACCOUNTS_LIST]),
    }),
    autoCreateMerchantsBankAccount: builder.mutation<AutoCreateMerchantBankAccountResponse, { uuid: string }>({
      query: ({ uuid }) => ({
        url: Endpoint.Merchants.BankAccountsByIdAutoCreate(uuid),
        method: HTTPMethod.POST,
      }),
      invalidatesTags: (result, error) => (error ? [] : [{ type: TAGS.MERCHANT_BANK_ACCOUNTS_LIST }]),
    }),
    updateMerchantKyb: builder.mutation<Response<SuccessResponse>, Partial<FormType>>({
      query: ({ uuid, ...body }) => ({
        url: Endpoint.Merchants.Kyb(uuid!),
        method: HTTPMethod.PUT,
        body,
      }),
      invalidatesTags: (result, error) => (error ? [] : [TAGS.MERCHANT_KYB]),
    }),
    triggerAutoKyb: builder.mutation<Response<SuccessResponse>, { uuid: string }>({
      query: ({ uuid }) => ({
        url: Endpoint.Merchants.AutoKyb(uuid),
        method: HTTPMethod.POST,
      }),
      invalidatesTags: (_, error) => (error ? [] : [TAGS.MERCHANT_KYB]),
    }),
    getBridgeKycInfo: builder.query<BridgeKycInfoData | null | undefined, string>({
      query: (uuid) => ({
        url: Endpoint.Merchants.BridgeKycInfo(uuid),
        method: HTTPMethod.GET,
      }),
      transformResponse: (response: BridgeKycInfoResponse) => response.data,
      providesTags: [TAGS.MERCHANT_KYB],
    }),
    getTitanData: builder.mutation<TitanKybDataResponse, { uuid: string; includeAllOwners?: boolean }>({
      query: ({ uuid, includeAllOwners }) => ({
        url: Endpoint.Merchants.TitanKybData(uuid, includeAllOwners),
        method: HTTPMethod.GET,
      }),
      invalidatesTags: (_, error) => (error ? [] : [TAGS.MERCHANT_KYB]),
    }),
    simulateDeposit: builder.mutation<SimulateDepositResponse, string>({
      query: (uuid) => ({
        url: Endpoint.Merchants.simulatePayout(uuid!),
        method: HTTPMethod.POST,
      }),
    }),
    updateMerchantPayoutSettings: builder.mutation<Response<SuccessResponse>, Partial<FiatPayoutSettingsPayload & { merchantId: string }>>({
      query: (requestBody) => ({
        url: Endpoint.Merchants.PayoutSettings(requestBody.merchantId!),
        method: HTTPMethod.PUT,
        body: omit(requestBody, 'merchantId'),
      }),
      invalidatesTags: (_, error) => (error ? [] : [TAGS.MERCHANT_KYB, TAGS.MERCHANT_BANK_ACCOUNTS_LIST]),
      transformResponse: (response: Response<SuccessResponse>) => ({
        data: response.data,
        requestId: response.requestId,
      }),
    }),
    getMerchantsPayoutDestinations: builder.query<MerchantsPayoutDestinationsResponse, string>({
      query: (uuid) => Endpoint.Merchants.PayoutDestinations(uuid),
      providesTags: [TAGS.MERCHANT_BANK_ACCOUNTS_LIST],
    }),
    deletePayoutDestination: builder.mutation<
      Response<SuccessResponse>,
      { merchantUuid: string; payoutDestinationUuid: string; payoutDestinationType: PayoutDestinationType }
    >({
      query: ({ merchantUuid, payoutDestinationUuid, payoutDestinationType }) => ({
        url: Endpoint.Merchants.DeletePayoutDestination(merchantUuid, payoutDestinationUuid, payoutDestinationType),
        method: HTTPMethod.DELETE,
      }),
      invalidatesTags: (_, error) => (error ? [] : [TAGS.MERCHANT_BANK_ACCOUNTS_LIST, TAGS.MERCHANT_KYB]),
    }),
    getRefundReserve: builder.query<RefundReserveResponse | undefined, string>({
      query: (uuid) => ({
        url: Endpoint.Merchants.RefundReserve(uuid),
        method: HTTPMethod.GET,
      }),
      transformResponse: (response: Response<RefundReserveResponse>) => response.data,
      providesTags: [TAGS.REFUND_RESERVE],
    }),
    createCryptoPayout: builder.mutation<CryptoPayoutResponse, CryptoPayoutRequest & { merchantUuid: string }>({
      query: ({ merchantUuid, ...payload }) => ({
        url: Endpoint.CryptoPayouts.Create,
        method: HTTPMethod.POST,
        body: { merchantUuid, ...payload },
      }),
    }),
    getAssociatedPersonsList: builder.query<AssociatedPerson[], string>({
      query: (merchantUuid) => ({
        url: Endpoint.Merchants.AssociatedPersonsList(merchantUuid),
        method: HTTPMethod.GET,
      }),
      transformResponse: (response: AssociatedPersonsListResponse) => response.data?.data || [],
      providesTags: [TAGS.ASSOCIATED_PERSONS],
    }),
    getAssociatedPersonById: builder.query<AssociatedPerson | undefined, { merchantUuid: string; personUuid: string }>({
      query: ({ merchantUuid, personUuid }) => ({
        url: Endpoint.Merchants.AssociatedPersonById(merchantUuid, personUuid),
        method: HTTPMethod.GET,
      }),
      transformResponse: (response: AssociatedPersonResponse) => response.data,
      providesTags: [TAGS.ASSOCIATED_PERSONS],
    }),
    createAssociatedPerson: builder.mutation<CreateAssociatedPersonResponse, CreateAssociatedPersonPayload & { merchantUuid: string }>({
      query: ({ merchantUuid, ...body }) => ({
        url: Endpoint.Merchants.CreateAssociatedPerson(merchantUuid),
        method: HTTPMethod.POST,
        body,
      }),
      invalidatesTags: (_, error) => (error ? [] : [TAGS.ASSOCIATED_PERSONS, TAGS.MERCHANT_KYB]),
    }),
    updateAssociatedPerson: builder.mutation<
      UpdateAssociatedPersonResponse,
      UpdateAssociatedPersonPayload & { merchantUuid: string; personUuid: string }
    >({
      query: ({ merchantUuid, personUuid, ...body }) => ({
        url: Endpoint.Merchants.UpdateAssociatedPerson(merchantUuid, personUuid),
        method: HTTPMethod.PUT,
        body,
      }),
      invalidatesTags: (_, error) => (error ? [] : [TAGS.ASSOCIATED_PERSONS, TAGS.MERCHANT_KYB]),
    }),
    deleteAssociatedPerson: builder.mutation<Response<SuccessResponse>, { merchantUuid: string; personUuid: string }>({
      query: ({ merchantUuid, personUuid }) => ({
        url: Endpoint.Merchants.DeleteAssociatedPerson(merchantUuid, personUuid),
        method: HTTPMethod.DELETE,
      }),
      invalidatesTags: (_, error) => (error ? [] : [TAGS.ASSOCIATED_PERSONS, TAGS.MERCHANT_KYB]),
    }),
  }),
});

export const { getMerchantsList, getMerchant } = merchantsApi.endpoints;
export const {
  useGetMerchantsListQuery,
  useLazyGetMerchantsListQuery,
  useGetMerchantQuery,
  useGetMerchantOnboardingTokenQuery,
  useGetBridgeKycInfoQuery,
  useUpdateMerchantMutation,
  useCreateMerchantMutation,
  useCreateMerchantsBankAccountMutation,
  useAutoCreateMerchantsBankAccountMutation,
  useUpdateMerchantKybMutation,
  useTriggerAutoKybMutation,
  useGetTitanDataMutation,
  useSimulateDepositMutation,
  useGetMerchantsPayoutDestinationsQuery,
  useDeletePayoutDestinationMutation,
  useGetRefundReserveQuery,
  useCreateCryptoPayoutMutation,
  useGetAssociatedPersonsListQuery,
  useLazyGetAssociatedPersonsListQuery,
  useGetAssociatedPersonByIdQuery,
  useLazyGetAssociatedPersonByIdQuery,
  useCreateAssociatedPersonMutation,
  useUpdateAssociatedPersonMutation,
  useDeleteAssociatedPersonMutation,
} = merchantsApi;
export default merchantsApi;
